import { Fragment } from "react";
import { Dialog, Menu, Popover, Transition } from "@headlessui/react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  BuildingOffice2Icon,
  CheckIcon,
  SunIcon,
  MoonIcon,
  ChevronDownIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  EllipsisHorizontalIcon,
  UserIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useSettings } from "../../contexts/settings-context";
import "./home/dashboard-design.css";
import { Logo } from "../utils/logo";
import { TWclassNames } from "../utils/Div";
import SidebarNav from "./sidebar-nav";
import { GlobalSearch } from "./global-search/GlobalSearch";
import {
  dashboardLogoVisibilityClass,
  dashboardMobileChromeClass,
  topBarUserInitials,
} from "./dashboard-nav-breakpoints";

export const USER_NAVIGATION = [
  { titleKey: "dashboard.header.your_profile", href: "/dashboard/user/profile" },
  { titleKey: "dashboard.header.settings", href: "/dashboard/user/settings" },
  {
    titleKey: "dashboard.header.microsoft_outlook",
    href: "/dashboard/user/microsoft",
  },
];

const iconButtonClass =
  "pd-header-icon inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2";

const panelClass =
  "z-[70] mt-2 origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-slate-900/5 focus:outline-none";

function TaskCount({ count, tone = "slate" }) {
  const tones = {
    slate: "text-slate-900",
    orange: "text-orange-600",
    red: "text-red-600",
  };
  return (
    <span className={TWclassNames("text-sm font-semibold tabular-nums", tones[tone])}>
      {count}
    </span>
  );
}

function TasksPanel({ loadingTasks, taskSummary, onGoToTasks, t }) {
  const unfinishedCount = taskSummary.unfinished;
  const dueTodayCount = taskSummary.due_today;
  const overdueCount = taskSummary.overdue;

  return (
    <>
      <div className="border-b border-slate-100 px-4 py-3">
        <h3 className="text-sm font-semibold text-slate-900">
          {t("dashboard.header.tasks")}
        </h3>
      </div>
      {loadingTasks ? (
        <div className="px-4 py-3 text-sm text-slate-500">
          {t("dashboard.header.loading_tasks")}
        </div>
      ) : (
        <>
          <div className="space-y-1 px-4 py-2">
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-600">
                {t("dashboard.header.unfinished")}
              </span>
              <TaskCount count={unfinishedCount} />
            </div>
            {dueTodayCount > 0 && (
              <div className="flex items-center justify-between border-t border-slate-100 py-2">
                <span className="text-sm text-slate-600">
                  {t("dashboard.header.due_today")}
                </span>
                <TaskCount count={dueTodayCount} tone="orange" />
              </div>
            )}
            {overdueCount > 0 && (
              <div className="flex items-center justify-between border-t border-slate-100 py-2">
                <span className="text-sm text-slate-600">
                  {t("dashboard.header.overdue")}
                </span>
                <TaskCount count={overdueCount} tone="red" />
              </div>
            )}
          </div>
          <div className="border-t border-slate-100">
            <button
              type="button"
              onClick={onGoToTasks}
              className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm font-medium text-sky-700 hover:bg-slate-50"
            >
              <span>{t("dashboard.header.go_to_tasks")}</span>
              <ClipboardDocumentListIcon className="h-4 w-4" />
            </button>
          </div>
        </>
      )}
    </>
  );
}

function CompanyPanel({
  loadingCompanies,
  companies,
  onCompanySwitch,
  t,
}) {
  if (loadingCompanies) {
    return (
      <div className="px-4 py-2 text-sm text-slate-500">
        {t("dashboard.header.loading_companies")}
      </div>
    );
  }
  if (companies.length === 0) {
    return (
      <div className="px-4 py-2 text-sm text-slate-500">
        {t("dashboard.header.no_companies")}
      </div>
    );
  }

  return companies.map((company) => (
    <button
      key={company.id}
      type="button"
      onClick={() => onCompanySwitch(company)}
      className={TWclassNames(
        "flex w-full items-center justify-between px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50",
        company.current ? "bg-sky-50 text-sky-900" : ""
      )}
    >
      <span className="min-w-0 flex-1 truncate">{company.name}</span>
      {company.current && <CheckIcon className="ml-2 h-4 w-4 shrink-0 text-sky-600" />}
    </button>
  ));
}

function TaskBadge({ count }) {
  if (!count) return null;
  return (
    <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white">
      {count > 9 ? "9+" : count}
    </span>
  );
}

export function DashboardTopBar({
  immersiveViewerOpen,
  drawingViewerOpen,
  sidebarOpen,
  setSidebarOpen,
  companies,
  loadingCompanies,
  currentCompany,
  onCompanySwitch,
  taskSummary,
  loadingTasks,
  currentUser,
  onDrawerNavClick,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { settings, saveSettings } = useSettings();
  const mobileChromeClass = dashboardMobileChromeClass(immersiveViewerOpen);
  const unfinishedCount = taskSummary.unfinished;
  const userName = currentUser?.name || currentUser?.displayTitle || "";
  const userEmail = currentUser?.email || "";
  const initials = topBarUserInitials(currentUser);
  const avatarUrl = currentUser?.image_url;

  const goToTasks = (close) => {
    close?.();
    navigate("/dashboard/todos");
  };

  const switchCompany = (company, close) => {
    close?.();
    onCompanySwitch(company);
  };

  return (
    <>
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className={TWclassNames("relative z-[80]", mobileChromeClass)}
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[1px]" />
          </Transition.Child>

          <div className="fixed inset-0 z-[80] flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-200 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-150 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="pd-theme pd-sidebar-drawer relative flex w-full max-w-xs flex-1 flex-col bg-white shadow-xl" data-theme={settings?.theme === "dark" ? "dark" : "light"}>
                <div className="pd-sidebar-drawer__header flex h-16 shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-4">
                  <Link to="/dashboard/" onClick={() => setSidebarOpen(false)} className="pd-brand" aria-label="Promang">
                    <Logo color={settings?.theme === "dark" ? "light" : "dark"} sizeClass="h-8" />
                  </Link>
                  <button
                    type="button"
                    className={iconButtonClass}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sr-only">
                      {t("dashboard.header.close_sidebar")}
                    </span>
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                <nav className="flex min-h-0 flex-1 flex-col overflow-y-auto px-2 py-3">
                  <SidebarNav onClick={onDrawerNavClick} collapsed={false} />
                </nav>
              </Dialog.Panel>
            </Transition.Child>
            <div className="w-14 shrink-0" aria-hidden="true" />
          </div>
        </Dialog>
      </Transition.Root>

      <header data-theme={settings.theme} className="pd-theme pd-topbar sticky top-0 z-50 flex h-16 min-w-0 max-w-full shrink-0 items-center justify-between gap-2 border-b border-slate-200/80 bg-white/90 px-2 backdrop-blur-md sm:px-4">
        <div className="flex min-w-0 items-center gap-1 sm:gap-3">
          <button
            type="button"
            className={TWclassNames(iconButtonClass, mobileChromeClass)}
            onClick={() => setSidebarOpen(true)}
            aria-label={t("dashboard.header.open_menu")}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <Link
            to="/dashboard/"
            className={TWclassNames(
              "min-w-0 items-center",
              dashboardLogoVisibilityClass(immersiveViewerOpen)
            )}
            aria-label="Promang"
          >
            <Logo color={settings.theme === "dark" ? "light" : "dark"} sizeClass="h-8" />
          </Link>
          {/^\/dashboard\/?$/.test(pathname) && (
            <span className="pd-header-label">/ {t("dashboardDesign.title")}</span>
          )}
        </div>

        <div className="flex min-w-0 items-center gap-0.5 sm:gap-1">
          <div className={drawingViewerOpen ? "hidden xl:block" : undefined}>
            <GlobalSearch />
          </div>

          <div className="hidden items-center gap-1 lg:flex">
            <Popover className="relative">
              <Popover.Button
                className={TWclassNames(iconButtonClass, "relative")}
                aria-label={t("dashboard.header.view_tasks")}
              >
                <ClipboardDocumentListIcon className="h-5 w-5" />
                <TaskBadge count={unfinishedCount} />
              </Popover.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Popover.Panel className={TWclassNames("absolute right-0 w-80", panelClass)}>
                  {({ close }) => (
                    <TasksPanel
                      loadingTasks={loadingTasks}
                      taskSummary={taskSummary}
                      onGoToTasks={() => goToTasks(close)}
                      t={t}
                    />
                  )}
                </Popover.Panel>
              </Transition>
            </Popover>

            <Popover className="relative">
              <Popover.Button
                className="inline-flex h-10 max-w-[min(16rem,28vw)] items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
                aria-label={t("dashboard.header.switch_company")}
              >
                <BuildingOffice2Icon className="h-5 w-5 shrink-0 text-slate-500" />
                <span className="hidden min-w-0 truncate xl:inline">
                  {currentCompany?.name || t("dashboard.header.companies")}
                </span>
                <ChevronDownIcon className="hidden h-4 w-4 shrink-0 text-slate-400 xl:inline" />
              </Popover.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Popover.Panel className={TWclassNames("absolute right-0 w-72 max-h-96 overflow-y-auto", panelClass)}>
                  {({ close }) => (
                    <>
                      <div className="border-b border-slate-100 px-4 py-3">
                        <h3 className="text-sm font-semibold text-slate-900">
                          {t("dashboard.header.switch_company")}
                        </h3>
                        {currentCompany?.name && (
                          <p className="mt-0.5 truncate text-xs text-slate-500">
                            {currentCompany.name}
                          </p>
                        )}
                      </div>
                      <CompanyPanel
                        loadingCompanies={loadingCompanies}
                        companies={companies}
                        onCompanySwitch={(company) => switchCompany(company, close)}
                        t={t}
                      />
                    </>
                  )}
                </Popover.Panel>
              </Transition>
            </Popover>
          </div>

          <Popover
            as="div"
            className={TWclassNames(
              "relative",
              drawingViewerOpen ? "hidden" : "lg:hidden"
            )}
          >
            <Popover.Button
              className={TWclassNames(iconButtonClass, "relative")}
              aria-label={t("dashboard.header.more_actions")}
            >
              <EllipsisHorizontalIcon className="h-6 w-6" />
              {unfinishedCount > 0 && (
                <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white" />
              )}
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Popover.Panel
                className={TWclassNames(
                  "absolute right-0 max-h-[min(70vh,28rem)] w-[min(20rem,calc(100vw-1rem))] overflow-y-auto",
                  panelClass
                )}
              >
                {({ close }) => (
                  <>
                    <TasksPanel
                      loadingTasks={loadingTasks}
                      taskSummary={taskSummary}
                      onGoToTasks={() => goToTasks(close)}
                      t={t}
                    />
                    <div className="border-t border-slate-200 px-4 py-3">
                      <h3 className="text-sm font-semibold text-slate-900">
                        {t("dashboard.header.switch_company")}
                      </h3>
                      {currentCompany?.name && (
                        <p className="mt-0.5 truncate text-xs text-slate-500">
                          {currentCompany.name}
                        </p>
                      )}
                    </div>
                    <CompanyPanel
                      loadingCompanies={loadingCompanies}
                      companies={companies}
                      onCompanySwitch={(company) => switchCompany(company, close)}
                      t={t}
                    />
                  </>
                )}
              </Popover.Panel>
            </Transition>
          </Popover>

          <div className="pd-theme-toggle" role="group" aria-label={t("dashboardDesign.theme")}>
            <button type="button" title={t("dashboardDesign.light")} aria-label={t("dashboardDesign.light")} aria-pressed={settings.theme !== "dark"} onClick={() => saveSettings({ ...settings, theme: "light" })}><SunIcon /></button>
            <button type="button" title={t("dashboardDesign.dark")} aria-label={t("dashboardDesign.dark")} aria-pressed={settings.theme === "dark"} onClick={() => saveSettings({ ...settings, theme: "dark" })}><MoonIcon /></button>
          </div>
          <Menu as="div" className="relative">
            <Menu.Button
              className={TWclassNames(iconButtonClass, "overflow-hidden")}
              aria-label={t("dashboard.header.open_user_menu")}
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt=""
                  className="h-8 w-8 rounded-lg object-cover"
                />
              ) : (
                <span className="pd-avatar flex h-8 w-8 items-center justify-center rounded-lg bg-sky-600 text-[11px] font-semibold text-white">
                  {initials}
                </span>
              )}
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items
                className={TWclassNames("absolute right-0 w-56 py-1", panelClass)}
              >
                {(userName || userEmail) && (
                  <div className="border-b border-slate-100 px-4 py-3">
                    {userName ? (
                      <p className="truncate text-sm font-medium text-slate-900">
                        {userName}
                      </p>
                    ) : null}
                    {userEmail ? (
                      <p className="truncate text-xs text-slate-500">{userEmail}</p>
                    ) : null}
                  </div>
                )}
                {USER_NAVIGATION.map((item) => (
                  <Menu.Item key={item.titleKey}>
                    {({ active }) => (
                      <Link
                        to={item.href}
                        className={TWclassNames(
                          active ? "bg-slate-50" : "",
                          "flex items-center gap-2 px-4 py-2 text-sm text-slate-700"
                        )}
                      >
                        {item.titleKey === "dashboard.header.settings" ? (
                          <Cog6ToothIcon className="h-4 w-4 text-slate-400" />
                        ) : (
                          <UserIcon className="h-4 w-4 text-slate-400" />
                        )}
                        {t(item.titleKey)}
                      </Link>
                    )}
                  </Menu.Item>
                ))}
                <div className="my-1 border-t border-slate-100" />
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/authentication/logout"
                      className={TWclassNames(
                        active ? "bg-slate-50" : "",
                        "flex items-center gap-2 px-4 py-2 text-sm text-red-600"
                      )}
                    >
                      <ArrowRightOnRectangleIcon className="h-4 w-4" />
                      {t("dashboard.header.sign_out")}
                    </Link>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </header>
    </>
  );
}

export default DashboardTopBar;
